// Shell UI state: theme, sidebar, command palette, drill panel stack.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark';

export interface DrillRequest {
  id: string;
  params: Record<string, string>;
}

interface UiState {
  theme: Theme;
  sidebarCollapsed: boolean;
  paletteOpen: boolean;
  alertsOpen: boolean;
  drillStack: DrillRequest[];
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
  toggleSidebar: () => void;
  setPaletteOpen: (open: boolean) => void;
  setAlertsOpen: (open: boolean) => void;
  openDrill: (id: string, params?: Record<string, string>) => void;
  pushDrill: (id: string, params?: Record<string, string>) => void;
  popDrill: (to?: number) => void;
  closeDrill: () => void;
}

export const useUi = create<UiState>()(
  persist(
    (set) => ({
      theme: 'light',
      sidebarCollapsed: false,
      paletteOpen: false,
      alertsOpen: false,
      drillStack: [],
      toggleTheme: () => set((s) => ({ theme: s.theme === 'dark' ? 'light' : 'dark' })),
      setTheme: (theme) => set({ theme }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
      setAlertsOpen: (alertsOpen) => set({ alertsOpen }),
      openDrill: (id, params = {}) => set({ drillStack: [{ id, params }] }),
      // Max 3 levels (KPI -> breakdown -> records), per the design spec.
      pushDrill: (id, params = {}) => set((s) => ({ drillStack: [...s.drillStack.slice(-2), { id, params }] })),
      popDrill: (to) => set((s) => ({ drillStack: s.drillStack.slice(0, to ?? s.drillStack.length - 1) })),
      closeDrill: () => set({ drillStack: [] }),
    }),
    { name: 'hx.ui', version: 1, partialize: (s) => ({ theme: s.theme, sidebarCollapsed: s.sidebarCollapsed }) },
  ),
);
